'use server'

import type { Indirizzo, Marche, MetodoSpedizione, Prodotto, Tinte } from '@/payload-types'

import configPromise from '@payload-config'
import { getPayload } from 'payload'

import { getCliente } from '@/clienti/getCliente'
import { totaleOrdine, totaleRiga, type TotaleOrdine } from '@/negozio/prezzi'
import { provinciaValida } from '@/negozio/province'
import { calcolaSpedizione, type Destinazione, type StimaSpedizione } from '@/negozio/spedizione'
import {
  chiaveRiga,
  normalizzaRighe,
  QUANTITA_MAX,
  type RigaCarrello,
  type TintaLibera,
} from './righe'

type Formato = NonNullable<Prodotto['formati']>[number]

export type MotivoRimozione =
  | 'non-valida'
  | 'sconosciuto'
  | 'non-attivo'
  | 'esaurito'
  | 'tinta-mancante'
  | 'tinta-sconosciuta'
  | 'tinta-incompatibile'

export type RigaRimossa = {
  /** The line as received, for display; `null` when it couldn't even be parsed. */
  riga: RigaCarrello | null
  motivo: MotivoRimozione
  messaggio: string
  /** Product name, when the SKU was found. */
  nome?: string
}

export type RigaValidata = RigaCarrello & {
  chiave: string
  prodotto: Pick<Prodotto, 'id' | 'nome' | 'prezziIndicativi' | 'slug' | 'tipoArt'>
  formato: Pick<Formato, 'disponibilita' | 'etichetta' | 'pesoGrammi' | 'volumeMl'>
  /** Unit price in cents, VAT included, read from the database. */
  prezzoCentesimi: number
  aliquotaIva: number
  totaleCentesimi: number
  /** The quantity asked for exceeded the stock and was lowered. */
  quantitaRidotta?: boolean
  tintaInfo?: Pick<Tinte, 'codice' | 'hex' | 'id' | 'nome' | 'slug' | 'tipo'> & {
    marca: string
  }
}

export type OpzioneSpedizione = Pick<MetodoSpedizione, 'id' | 'nome' | 'tempiConsegna' | 'tipo'> & {
  stima: StimaSpedizione
}

export type CarrelloValidato = {
  righe: RigaValidata[]
  rimosse: RigaRimossa[]
  totale: TotaleOrdine
  /** Sum of the quantities: the header counter. */
  articoli: number
  prezziIndicativi: boolean
  /** Every active shipping method with its estimate for these lines, in admin order. */
  spedizione: OpzioneSpedizione[]
  /** Where the estimate was computed for: the argument, else the cliente's default address. */
  destinazione: Destinazione | null
}

const messaggi: Record<MotivoRimozione, string> = {
  'non-valida': 'Riga non valida.',
  sconosciuto: 'Prodotto non più disponibile nel catalogo.',
  'non-attivo': 'Questo formato non è più in vendita.',
  esaurito: 'Formato esaurito.',
  'tinta-mancante':
    'Manca la tinta (marca, modello e codice colore): questo prodotto viene preparato nel colore richiesto.',
  'tinta-sconosciuta': 'Tinta non trovata nel catalogo.',
  'tinta-incompatibile': 'Questa tinta non si può produrre in questo formato.',
}

/**
 * Re-reads every cart line from the database: the only prices, names and availability the
 * cart page ever shows. Lines are dropped (and listed in `rimosse`) when the SKU is unknown
 * or inactive, sold out, or when the paint can't be made in that product (e.g. a three-coat
 * paint in a spray can, via `prodotti.tipiTintaEsclusi`). Anything the client sends besides
 * SKU, quantity and paint — a price, for instance — is ignored.
 */
export async function validaCarrello(
  input: unknown,
  destinazioneRichiesta?: unknown,
): Promise<CarrelloValidato> {
  const { righe, scartate } = normalizzaRighe(input)
  const rimosse: RigaRimossa[] = scartate.map(() => ({
    riga: null,
    motivo: 'non-valida',
    messaggio: messaggi['non-valida'],
  }))

  const payload = await getPayload({ config: configPromise })
  const skus = [...new Set(righe.map((r) => r.sku))]
  const tintaIds = [...new Set(righe.flatMap((r) => (r.tinta ? [r.tinta] : [])))]

  const [{ docs: prodotti }, { docs: tinte }] = await Promise.all([
    skus.length
      ? payload.find({
          collection: 'prodotti',
          depth: 0,
          draft: false,
          limit: skus.length,
          overrideAccess: false,
          pagination: false,
          select: {
            formati: true,
            nome: true,
            prezziIndicativi: true,
            slug: true,
            tipiTintaEsclusi: true,
            tipoArt: true,
            tipoProdotto: true,
          },
          where: {
            and: [{ 'formati.sku': { in: skus } }, { _status: { equals: 'published' } }],
          },
        })
      : { docs: [] as Prodotto[] },
    tintaIds.length
      ? payload.find({
          collection: 'tinte',
          depth: 1,
          limit: tintaIds.length,
          overrideAccess: false,
          pagination: false,
          populate: { marche: { nome: true } },
          select: { codice: true, hex: true, marca: true, nome: true, slug: true, tipo: true },
          where: { id: { in: tintaIds } },
        })
      : { docs: [] as Tinte[] },
  ])

  const perSku = new Map<string, { prodotto: Prodotto; formato: Formato }>()
  for (const prodotto of prodotti as Prodotto[]) {
    for (const formato of prodotto.formati ?? []) perSku.set(formato.sku, { prodotto, formato })
  }
  const perTinta = new Map((tinte as Tinte[]).map((t) => [t.id, t]))

  const valide = new Map<string, RigaValidata>()
  const rimuovi = (riga: RigaCarrello, motivo: MotivoRimozione, nome?: string) =>
    rimosse.push({ riga, motivo, messaggio: messaggi[motivo], ...(nome && { nome }) })

  for (const riga of righe) {
    const trovato = perSku.get(riga.sku)
    if (!trovato) {
      rimuovi(riga, 'sconosciuto')
      continue
    }
    const { prodotto, formato } = trovato
    const giacenza = typeof formato.giacenza === 'number' ? formato.giacenza : null

    if (formato.attivo === false) {
      rimuovi(riga, 'non-attivo', prodotto.nome)
      continue
    }
    if (formato.disponibilita === 'esaurito' || (giacenza !== null && giacenza <= 0)) {
      rimuovi(riga, 'esaurito', prodotto.nome)
      continue
    }

    let tinta: number | undefined
    let veicolo: string | undefined
    let tintaLibera: TintaLibera | undefined
    let tintaInfo: RigaValidata['tintaInfo']

    if (prodotto.tipoProdotto === 'su-misura') {
      if (riga.tinta) {
        const doc = perTinta.get(riga.tinta)
        if (!doc) {
          rimuovi(riga, 'tinta-sconosciuta', prodotto.nome)
          continue
        }
        if (prodotto.tipiTintaEsclusi?.includes(doc.tipo)) {
          rimuovi(riga, 'tinta-incompatibile', prodotto.nome)
          continue
        }
        tinta = doc.id
        veicolo = riga.veicolo
        tintaInfo = {
          codice: doc.codice,
          hex: doc.hex,
          id: doc.id,
          marca:
            typeof doc.marca === 'object'
              ? (doc.marca as Marche).nome
              : (doc.marcaSlug ?? '').toUpperCase(),
          nome: doc.nome,
          slug: doc.slug,
          tipo: doc.tipo,
        }
      } else if (riga.tintaLibera) {
        // Typed by hand: nothing to check against, the shop confirms it with the customer.
        // `normalizzaRighe` only keeps it with brand, code and vehicle model.
        tintaLibera = riga.tintaLibera
      } else {
        rimuovi(riga, 'tinta-mancante', prodotto.nome)
        continue
      }
    }
    // Standard products: a paint, if any was sent, is meaningless and dropped.

    const pulita: RigaCarrello = {
      sku: riga.sku,
      quantita: riga.quantita,
      ...(tinta && { tinta }),
      ...(veicolo && { veicolo }),
      ...(tintaLibera && { tintaLibera }),
    }
    const chiave = chiaveRiga(pulita)
    const quantitaRichiesta = Math.min(
      (valide.get(chiave)?.quantita ?? 0) + pulita.quantita,
      QUANTITA_MAX,
    )
    const quantita = giacenza !== null ? Math.min(quantitaRichiesta, giacenza) : quantitaRichiesta

    valide.set(chiave, {
      ...pulita,
      chiave,
      quantita,
      ...(quantita < quantitaRichiesta && { quantitaRidotta: true }),
      prodotto: {
        id: prodotto.id,
        nome: prodotto.nome,
        prezziIndicativi: prodotto.prezziIndicativi,
        slug: prodotto.slug,
        tipoArt: prodotto.tipoArt,
      },
      formato: {
        disponibilita: formato.disponibilita,
        etichetta: formato.etichetta,
        pesoGrammi: formato.pesoGrammi,
        volumeMl: formato.volumeMl,
      },
      prezzoCentesimi: formato.prezzoCentesimi,
      aliquotaIva: formato.aliquotaIva,
      totaleCentesimi: totaleRiga({ ...formato, quantita }),
      ...(tintaInfo && { tintaInfo }),
    })
  }

  const righeValide = [...valide.values()]
  const totale = totaleOrdine(righeValide)
  const richiesta = normalizzaDestinazione(destinazioneRichiesta)

  const [destinazione, { docs: metodi }] = await Promise.all([
    richiesta === undefined ? destinazioneCliente() : richiesta,
    payload.find({
      collection: 'metodi-spedizione',
      depth: 0,
      overrideAccess: false,
      pagination: false,
      sort: 'ordinamento',
      where: { attivo: { equals: true } },
    }),
  ])
  const righeSpedizione = righeValide.map((r) => ({
    quantita: r.quantita,
    pesoGrammi: r.formato.pesoGrammi,
    volumeMl: r.formato.volumeMl,
  }))

  return {
    righe: righeValide,
    rimosse,
    totale,
    articoli: righeValide.reduce((n, r) => n + r.quantita, 0),
    prezziIndicativi: righeValide.some((r) => r.prodotto.prezziIndicativi),
    spedizione: (metodi as MetodoSpedizione[]).map((metodo) => ({
      id: metodo.id,
      nome: metodo.nome,
      tempiConsegna: metodo.tempiConsegna,
      tipo: metodo.tipo,
      stima: calcolaSpedizione({
        righe: righeSpedizione,
        indirizzo: destinazione,
        metodo,
        totaleMerceCentesimi: totale.lordo,
      }),
    })),
    destinazione,
  }
}

/**
 * `{ provincia, cap }` from the client, sanitized. `undefined` when no object was sent (use the
 * cliente's address), `null` when the customer cleared both fields (no destination).
 */
const normalizzaDestinazione = (input: unknown): Destinazione | null | undefined => {
  if (!input || typeof input !== 'object') return undefined
  const { provincia, cap } = input as Record<string, unknown>
  const sigla = typeof provincia === 'string' ? provincia.trim().toUpperCase() : ''
  const codice = typeof cap === 'string' ? cap.replace(/\s+/g, '') : ''
  const destinazione: Destinazione = {
    ...(provinciaValida(sigla) && { provincia: sigla }),
    ...(/^\d{5}$/.test(codice) && { cap: codice }),
  }
  return destinazione.provincia || destinazione.cap ? destinazione : null
}

/** The logged-in cliente's default shipping address, if any. */
const destinazioneCliente = async (): Promise<Destinazione | null> => {
  const cliente = await getCliente()
  if (!cliente) return null
  const payload = await getPayload({ config: configPromise })
  const { docs } = await payload.find({
    collection: 'indirizzi',
    depth: 0,
    limit: 1,
    overrideAccess: false,
    select: { cap: true, nazione: true, provincia: true },
    user: cliente,
    where: { predefinitoSpedizione: { equals: true } },
  })
  const indirizzo = docs[0] as Pick<Indirizzo, 'cap' | 'nazione' | 'provincia'> | undefined
  return indirizzo
    ? { cap: indirizzo.cap, nazione: indirizzo.nazione, provincia: indirizzo.provincia }
    : null
}
